import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import axiosInstance from '../../../utils/axios';
import { toast } from 'react-hot-toast';

const AuditQueue = ({ onBulkVerify, filters }) => {
  const [loading, setLoading] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const [queueData, setQueueData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
  });

  // Modal states
  const [verifyModal, setVerifyModal] = useState({
    isOpen: false,
    asset: null,
    assetIds: [],
    auditorName: '',
    condition: 'Optimal',
    auditResult: 'Verified',
    notes: 'Physical barcode verified. Hardware status confirmed.',
    submitting: false,
  });

  const [exceptionModal, setExceptionModal] = useState({
    isOpen: false,
    asset: null,
    exception_type: 'location_mismatch',
    severity: 'medium',
    description: '',
    assigned_to: '',
    submitting: false,
  });

  // Dedicated dynamic auditor users list
  const [auditors, setAuditors] = useState([]);

  useEffect(() => {
    const fetchAuditors = async () => {
      try {
        const res = await axiosInstance.get('/audit/auditors');
        if (res.data?.success && Array.isArray(res.data.data)) {
          setAuditors(res.data.data);
          if (res.data.data.length > 0) {
            setVerifyModal((prev) => ({
              ...prev,
              auditorName: res.data.data[0].name,
            }));
          }
        }
      } catch (err) {
        console.error('Failed to load auditors:', err);
      }
    };
    fetchAuditors();
  }, []);

  const loadQueue = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const params = {
          page: page,
          per_page: pagination.per_page,
          sort: sortConfig.key,
          direction: sortConfig.direction,
          ...filters,
        };

        const response = await axiosInstance.get('/audit/queue', { params });

        if (response.data.success) {
          setQueueData(response.data.data.data || []);
          setPagination({
            current_page: response.data.data.current_page || 1,
            per_page: response.data.data.per_page || 20,
            total: response.data.data.total || 0,
          });
        } else {
          toast.error('Failed to load audit queue');
        }
      } catch (error) {
        console.error('Error loading audit queue:', error);
        toast.error('Failed to load audit queue');
      } finally {
        setLoading(false);
      }
    },
    [filters, sortConfig, pagination.per_page]
  );

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = (event) => {
      const newFilters = event.detail || {};
      if (Object.keys(newFilters).length > 0) {
        loadQueue(1);
      } else {
        loadQueue(pagination.current_page);
      }
    };
    window.addEventListener('refreshAuditView', handleRefresh);
    return () => window.removeEventListener('refreshAuditView', handleRefresh);
  }, [loadQueue, pagination.current_page]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedAssets(queueData.map((asset) => asset.asset_id));
    } else {
      setSelectedAssets([]);
    }
  };

  const handleSelectAsset = (assetId, checked) => {
    if (checked) {
      setSelectedAssets((prev) => [...prev, assetId]);
    } else {
      setSelectedAssets((prev) => prev.filter((id) => id !== assetId));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-rose-500/10 text-rose-600 border border-rose-500/20';
      case 'high':
        return 'bg-amber-500/10 text-amber-600 border border-amber-500/20';
      case 'medium':
        return 'bg-blue-500/10 text-blue-600 border border-blue-500/20';
      default:
        return 'bg-slate-500/10 text-slate-600 border border-slate-500/20';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20';
      case 'in_progress':
        return 'bg-indigo-500/10 text-indigo-600 border border-indigo-500/20';
      case 'pending':
        return 'bg-amber-500/10 text-amber-600 border border-amber-500/20';
      case 'exception':
        return 'bg-rose-500/10 text-rose-600 border border-rose-500/20';
      case 'escalated':
        return 'bg-purple-500/10 text-purple-600 border border-purple-500/20';
      default:
        return 'bg-slate-500/10 text-slate-600 border border-slate-500/20';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never Verified';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success(`Copied ${text} to clipboard!`);
  };

  // Open Verify Modal
  const openSingleVerify = (asset) => {
    setVerifyModal({
      isOpen: true,
      asset: asset,
      assetIds: [asset.asset_id],
      auditorName: auditors.length > 0 ? auditors[0].name : 'Primary Auditor',
      condition: 'Optimal',
      auditResult: 'Verified',
      notes: `Physical verification confirmed for ${asset.asset_id} (${asset.brand || ''} ${asset.model || ''}).`,
      submitting: false,
    });
  };

  const openBulkVerifyModal = () => {
    if (selectedAssets.length === 0) {
      toast.error('Please select assets to verify');
      return;
    }
    setVerifyModal({
      isOpen: true,
      asset: null,
      assetIds: selectedAssets,
      auditorName: auditors.length > 0 ? auditors[0].name : 'Primary Auditor',
      condition: 'Optimal',
      auditResult: 'Verified',
      notes: `Bulk physical scan verified for ${selectedAssets.length} asset(s).`,
      submitting: false,
    });
  };

  const submitVerify = async () => {
    try {
      setVerifyModal((prev) => ({ ...prev, submitting: true }));
      const res = await axiosInstance.post('/audit/bulk-verify', {
        asset_ids: verifyModal.assetIds,
        auditor_name: verifyModal.auditorName,
        condition: verifyModal.condition,
        notes: verifyModal.notes,
      });

      if (res.data?.success) {
        toast.success(res.data.message || 'Assets successfully verified!');
        setVerifyModal((prev) => ({ ...prev, isOpen: false }));
        setSelectedAssets([]);
        loadQueue(pagination.current_page);

        // Notify parent and other tabs
        window.dispatchEvent(new CustomEvent('refreshAuditStats'));
      } else {
        toast.error(res.data?.message || 'Verification failed');
      }
    } catch (err) {
      console.error('Error submitting verification:', err);
      toast.error(err.response?.data?.message || 'Verification submission error');
    } finally {
      setVerifyModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  // Open Exception Modal
  const openFlagException = (asset) => {
    setExceptionModal({
      isOpen: true,
      asset: asset,
      exception_type: 'location_mismatch',
      severity: 'medium',
      description: `Asset ${asset.asset_id} reported with physical discrepancy during audit check.`,
      assigned_to: asset.assigned_to !== 'Unassigned' ? asset.assigned_to : 'Auditor Team',
      submitting: false,
    });
  };

  const submitException = async () => {
    try {
      setExceptionModal((prev) => ({ ...prev, submitting: true }));
      const res = await axiosInstance.post('/audit/exceptions', {
        asset_id: exceptionModal.asset.asset_id,
        exception_type: exceptionModal.exception_type,
        severity: exceptionModal.severity,
        description: exceptionModal.description,
        assigned_to: exceptionModal.assigned_to,
      });

      if (res.data?.success) {
        toast.success('Exception flagged successfully!');
        setExceptionModal((prev) => ({ ...prev, isOpen: false }));
        loadQueue(pagination.current_page);
        window.dispatchEvent(new CustomEvent('refreshAuditStats'));
      } else {
        toast.error(res.data?.message || 'Failed to flag exception');
      }
    } catch (err) {
      console.error('Error recording exception:', err);
      toast.error(err.response?.data?.message || 'Failed to record exception');
    } finally {
      setExceptionModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle overflow-hidden">
      {/* Table Header Bar */}
      <div className="p-4 sm:p-6 border-b border-border flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-card-foreground">Quarterly Audit Queue</h3>
            <span className="bg-primary/10 text-primary text-xs px-2.5 py-0.5 rounded-full font-semibold">
              {pagination.total} Total Items
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Physical hardware verification, barcode scanning, and asset compliance status
          </p>
        </div>

        <div className="flex items-center space-x-2 flex-wrap gap-2">
          {selectedAssets.length > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={openBulkVerifyModal}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
            >
              <Icon name="CheckCircle" size={15} className="mr-1.5" />
              Verify Selected ({selectedAssets.length})
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => loadQueue(pagination.current_page)}
            disabled={loading}
            className="h-9"
          >
            <Icon name="RefreshCw" size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main Table */}
      {loading && queueData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3"></div>
          <p className="text-sm text-muted-foreground">Loading audit inventory...</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 border-b border-border text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                <tr>
                  <th className="w-10 p-3.5 text-center">
                    <Checkbox
                      checked={selectedAssets.length === queueData.length && queueData.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th
                    className="p-3.5 cursor-pointer hover:text-foreground transition select-none"
                    onClick={() => handleSort('asset_id')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Asset Tag</span>
                      <Icon name="ArrowUpDown" size={12} />
                    </div>
                  </th>
                  <th
                    className="p-3.5 cursor-pointer hover:text-foreground transition select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Device & Model</span>
                      <Icon name="ArrowUpDown" size={12} />
                    </div>
                  </th>
                  <th className="p-3.5">Department & Location</th>
                  <th className="p-3.5">Custodian</th>
                  <th className="p-3.5">Valuation</th>
                  <th
                    className="p-3.5 cursor-pointer hover:text-foreground transition select-none"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Last Verified</span>
                      <Icon name="ArrowUpDown" size={12} />
                    </div>
                  </th>
                  <th className="p-3.5 text-center">Status</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {queueData.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-12">
                      <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mx-auto mb-3">
                        <Icon name="Inbox" size={24} className="text-muted-foreground" />
                      </div>
                      <p className="font-medium text-foreground">No assets match your audit filter</p>
                      <p className="text-xs text-muted-foreground mt-1">Try clearing or adjusting the search scope</p>
                    </td>
                  </tr>
                ) : (
                  queueData.map((asset) => (
                    <tr
                      key={asset.id}
                      className={`hover:bg-muted/30 transition-colors ${
                        selectedAssets.includes(asset.asset_id) ? 'bg-primary/5' : ''
                      }`}
                    >
                      <td className="p-3.5 text-center">
                        <Checkbox
                          checked={selectedAssets.includes(asset.asset_id)}
                          onCheckedChange={(checked) => handleSelectAsset(asset.asset_id, checked)}
                        />
                      </td>

                      {/* Asset Tag */}
                      <td className="p-3.5 whitespace-nowrap">
                        <div className="flex items-center space-x-1.5">
                          <span className="font-mono font-semibold text-foreground text-xs">
                            {asset.asset_id}
                          </span>
                          <button
                            onClick={() => copyToClipboard(asset.asset_id)}
                            className="text-muted-foreground hover:text-primary transition p-0.5 rounded"
                            title="Copy Asset Tag"
                          >
                            <Icon name="Copy" size={12} />
                          </button>
                        </div>
                        <span className="text-[11px] text-muted-foreground block">
                          {asset.asset_type}
                        </span>
                      </td>

                      {/* Device & Model */}
                      <td className="p-3.5">
                        <div className="font-medium text-foreground text-xs leading-tight">
                          {asset.name || `${asset.brand || ''} ${asset.model || ''}`}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          SN: <span className="font-mono">{asset.serial_number || 'N/A'}</span>
                        </div>
                      </td>

                      {/* Department & Location */}
                      <td className="p-3.5">
                        <div className="text-xs text-foreground font-medium">{asset.department || 'General'}</div>
                        <div className="text-[11px] text-muted-foreground flex items-center space-x-1 mt-0.5">
                          <Icon name="MapPin" size={11} />
                          <span>{asset.location}</span>
                        </div>
                      </td>

                      {/* Custodian */}
                      <td className="p-3.5 whitespace-nowrap">
                        <div className="text-xs font-medium text-foreground">{asset.assigned_to}</div>
                        {asset.assigned_email && (
                          <div className="text-[11px] text-muted-foreground">{asset.assigned_email}</div>
                        )}
                      </td>

                      {/* Valuation */}
                      <td className="p-3.5 whitespace-nowrap">
                        <span className="text-xs font-semibold text-foreground font-mono">
                          {asset.formatted_price || '₹0.00'}
                        </span>
                      </td>

                      {/* Last Verified */}
                      <td className="p-3.5 whitespace-nowrap">
                        <span className="text-xs text-muted-foreground block">
                          {formatDate(asset.last_verified)}
                        </span>
                        {asset.days_overdue > 0 && (
                          <span className="inline-block text-[10px] font-semibold text-rose-600 bg-rose-500/10 px-1.5 py-0.5 rounded mt-0.5">
                            {asset.days_overdue}d overdue
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="p-3.5 text-center whitespace-nowrap">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${getStatusBadge(
                            asset.status
                          )}`}
                        >
                          {asset.status.replace('_', ' ')}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="p-3.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end space-x-1.5">
                          {/* Single Verify */}
                          <button
                            onClick={() => openSingleVerify(asset)}
                            className="inline-flex items-center px-2 py-1 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 text-xs font-medium transition"
                            title="Perform Physical Verification"
                          >
                            <Icon name="CheckCircle" size={13} className="mr-1" />
                            Verify
                          </button>

                          {/* Flag Exception */}
                          <button
                            onClick={() => openFlagException(asset)}
                            className="inline-flex items-center px-2 py-1 rounded bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 text-xs font-medium transition"
                            title="Flag Discrepancy / Exception"
                          >
                            <Icon name="AlertTriangle" size={13} className="mr-1" />
                            Issue
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination.total > 0 && (
            <div className="p-4 border-t border-border flex items-center justify-between flex-wrap gap-4 text-xs text-muted-foreground">
              <div>
                Showing {(pagination.current_page - 1) * pagination.per_page + 1} to{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} assets
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.current_page <= 1}
                  onClick={() => loadQueue(pagination.current_page - 1)}
                  className="h-8 text-xs"
                >
                  Previous
                </Button>
                <span className="font-medium text-foreground px-1">
                  Page {pagination.current_page} of {Math.ceil(pagination.total / pagination.per_page)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.current_page >= Math.ceil(pagination.total / pagination.per_page)}
                  onClick={() => loadQueue(pagination.current_page + 1)}
                  className="h-8 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ========================================================================= */}
      {/* 1. PHYSICAL VERIFICATION MODAL */}
      {/* ========================================================================= */}
      {verifyModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 border-b border-border bg-muted/20 flex items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                  <Icon name="CheckCircle" size={18} />
                </div>
                <div>
                  <h3 className="font-semibold text-card-foreground text-base">
                    Physical Audit Verification
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {verifyModal.asset
                      ? `Verifying ${verifyModal.asset.asset_id} (${verifyModal.asset.name})`
                      : `Verifying ${verifyModal.assetIds.length} selected assets`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setVerifyModal((prev) => ({ ...prev, isOpen: false }))}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4 text-sm">
              {/* Auditor Name Selection (dynamically fetched from users table) */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Auditor Name <span className="text-rose-500">*</span>
                </label>
                {auditors.length > 0 ? (
                  <select
                    value={verifyModal.auditorName}
                    onChange={(e) =>
                      setVerifyModal((prev) => ({ ...prev, auditorName: e.target.value }))
                    }
                    className="w-full px-3 py-2 text-xs rounded-md border border-input bg-background text-foreground focus:ring-1 focus:ring-primary"
                  >
                    {auditors.map((auditor) => (
                      <option key={auditor.id} value={auditor.name}>
                        {auditor.name} ({auditor.department || auditor.email || 'Auditor'})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={verifyModal.auditorName}
                    onChange={(e) =>
                      setVerifyModal((prev) => ({ ...prev, auditorName: e.target.value }))
                    }
                    placeholder="Enter Auditor Name"
                    className="w-full px-3 py-2 text-xs rounded-md border border-input bg-background text-foreground"
                  />
                )}
              </div>

              {/* Physical Condition */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Physical Condition Assessment
                </label>
                <select
                  value={verifyModal.condition}
                  onChange={(e) =>
                    setVerifyModal((prev) => ({ ...prev, condition: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-xs rounded-md border border-input bg-background text-foreground focus:ring-1 focus:ring-primary"
                >
                  <option value="Optimal">Optimal (Excellent / Like New)</option>
                  <option value="Good">Good (Normal Working Condition)</option>
                  <option value="Minor Wear">Minor Wear (Cosmetic Blemishes)</option>
                  <option value="Needs Maintenance">Needs Maintenance / Service</option>
                  <option value="Damaged">Damaged / Non-Functional</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Verification Remarks & Notes
                </label>
                <textarea
                  rows={3}
                  value={verifyModal.notes}
                  onChange={(e) =>
                    setVerifyModal((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-xs rounded-md border border-input bg-background text-foreground focus:ring-1 focus:ring-primary"
                  placeholder="Record barcode match, physical inspection, battery/screen check..."
                />
              </div>
            </div>

            <div className="p-4 border-t border-border bg-muted/10 flex items-center justify-end space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setVerifyModal((prev) => ({ ...prev, isOpen: false }))}
                disabled={verifyModal.submitting}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={submitVerify}
                loading={verifyModal.submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              >
                <Icon name="Check" size={14} className="mr-1.5" />
                Confirm Verification
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. FLAG EXCEPTION MODAL */}
      {/* ========================================================================= */}
      {exceptionModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 border-b border-border bg-rose-500/10 flex items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 rounded-lg bg-rose-500 text-white flex items-center justify-center">
                  <Icon name="AlertTriangle" size={18} />
                </div>
                <div>
                  <h3 className="font-semibold text-card-foreground text-base">
                    Flag Audit Discrepancy
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Asset: {exceptionModal.asset?.asset_id} ({exceptionModal.asset?.name})
                  </p>
                </div>
              </div>
              <button
                onClick={() => setExceptionModal((prev) => ({ ...prev, isOpen: false }))}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Discrepancy Type
                  </label>
                  <select
                    value={exceptionModal.exception_type}
                    onChange={(e) =>
                      setExceptionModal((prev) => ({
                        ...prev,
                        exception_type: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 text-xs rounded-md border border-input bg-background text-foreground"
                  >
                    <option value="location_mismatch">Location Mismatch</option>
                    <option value="missing_asset">Missing / Unaccounted Asset</option>
                    <option value="damaged_asset">Damaged Hardware</option>
                    <option value="condition_issue">Condition / Battery Issue</option>
                    <option value="unauthorized_removal">Unauthorized Removal</option>
                    <option value="duplicate_entry">Duplicate Serial / ID</option>
                    <option value="other">Other Discrepancy</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Severity Level
                  </label>
                  <select
                    value={exceptionModal.severity}
                    onChange={(e) =>
                      setExceptionModal((prev) => ({
                        ...prev,
                        severity: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 text-xs rounded-md border border-input bg-background text-foreground"
                  >
                    <option value="low">Low Severity</option>
                    <option value="medium">Medium Severity</option>
                    <option value="high">High Severity</option>
                    <option value="critical">Critical (Immediate Escalation)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Assign Investigation To
                </label>
                <input
                  type="text"
                  value={exceptionModal.assigned_to}
                  onChange={(e) =>
                    setExceptionModal((prev) => ({
                      ...prev,
                      assigned_to: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 text-xs rounded-md border border-input bg-background text-foreground"
                  placeholder="Custodian or IT Support Lead"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Detailed Findings & Description <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={exceptionModal.description}
                  onChange={(e) =>
                    setExceptionModal((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 text-xs rounded-md border border-input bg-background text-foreground"
                  placeholder="Describe physical inspection discrepancies, missing peripherals, wrong floor..."
                />
              </div>
            </div>

            <div className="p-4 border-t border-border bg-muted/10 flex items-center justify-end space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExceptionModal((prev) => ({ ...prev, isOpen: false }))}
                disabled={exceptionModal.submitting}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={submitException}
                loading={exceptionModal.submitting}
                className="bg-rose-600 hover:bg-rose-700 text-white font-medium"
              >
                <Icon name="AlertCircle" size={14} className="mr-1.5" />
                Record Exception
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditQueue;
